import { window } from "global";
import { useState, useEffect } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useGlobal } from "reactn";
import { Helmet } from "react-helmet-async";
import { useMutation } from "@apollo/client";
import CustomInput from "components/CustomInput";
import PasswordChecker from "components/PasswordChecker";
import SummonerFinder from "components/SummonerFinder";
import ButtonSuccess from "components/Buttons/ButtonSuccess";

import { useInitiateOauthLoginLazyQuery } from "@ugitgud/legos/queries/auth";
import { parseQueryString } from "@ugg/shared/utils/url-query-params";
import { addCookie } from "@ugg/shared/utils/cookies";
import { isValidEmail, isValidPassword } from "lib/form-helpers";
import { CREATE_ACCOUNT } from "@ugg/shared/api/graphql/queries";
import { splitRiotId } from "@ugg/shared/utils/riot-id-helpers";
import { AppRoutes } from "@ugg/shared/routes/app-routes";

import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgLolDiscordIcon } from "@ugitgud/legos/ui/icon/SVG/LolDiscordIcon";
import { SvgBrandsGoogle } from "@ugitgud/legos/ui/icon/SVG/BrandsGoogle";
import { SvgBrandsRiot } from "@ugitgud/legos/ui/icon/SVG/BrandsRiot";

export const Signup = (props) => {
  const history = useHistory();
  const location = useLocation();
  const search = location.search.substring(1);
  const parsedQueryString = parseQueryString(search);
  const [clientRegion] = useGlobal("clientRegion");

  const [showPasswordChecker, setPasswordChecker] = useState(false);
  const [signupError, setSignupError] = useState("");

  // Form
  const [form, setForm] = useState({
    email: { value: "", error: "" },
    password: { value: "", error: "" },
    riotId: { value: "", error: "" },
    summonerRegion: { value: clientRegion || "", error: "" },
    marketingOptOut: false,
  });

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  const { email, password, riotId, summonerRegion, marketingOptOut } = form;

  const onCompleted = (data) => {
    const token = data.register.token;
    if (window) {
      addCookie("authToken", token);
      // localStorage.setItem("authToken", token);
      const signupVar = parsedQueryString.continue ? parsedQueryString.continue : "";
      const splitURL = signupVar.split("/");
      if (parsedQueryString.redirect_url) {
        window.location.href = `${parsedQueryString.redirect_url}`;
      } else if (splitURL.length === 1) {
        window.location.href = "/?onboarding=true";
      } else {
        window.location.href = `${AppRoutes.PREMIUM_CHECKOUT}?planType=${splitURL.pop()}`;
      }
    }
  };

  const onError = (apolloError) => {
    const error = JSON.parse(JSON.stringify(apolloError));
    const { graphQLErrors } = error;

    if (graphQLErrors[0].message === "duplicate_user") {
      setSignupError("An account with this E-mail already exists.");
      // Other errors
    } else {
      console.error(graphQLErrors);
      setSignupError("An unexpected error occurred. Please try again.");
    }
  };

  const [inputRiotUserName, inputRiotTagLine] = splitRiotId(riotId.value);
  const [createAccount, { loading: creatingAccount, data: createdAccount, error: errorCreateAccount }] = useMutation(
    CREATE_ACCOUNT,
    {
      onCompleted,
      onError,
      variables: {
        email: email.value,
        marketingOptOut: marketingOptOut,
        password: password.value,
        riotUserName: inputRiotUserName,
        riotTagLine: inputRiotTagLine,
        summonerRegion: summonerRegion.value,
      },
      context: { clientName: "auth-api" },
    },
  );

  const onInputChange = (key, value) => {
    setForm({ ...form, [key]: { ...form[key], ...value } });
  };

  const onPasswordFocus = () => {
    setPasswordChecker(true);
  };

  const onPasswordBlur = () => {
    setTimeout(() => {
      setPasswordChecker(false);
    }, 100);
  };

  const verifyForm = () => {
    let verifiedForm = {};
    let hasError = false;
    let foundValidationError = false;
    function mergeForm(state, error) {
      if (error) {
        hasError = true;
        foundValidationError = true;
      }
      return Object.assign(verifiedForm, state);
    }

    // Email
    if (email.value.trim().length === 0) {
      verifiedForm = mergeForm({ email: { ...email, error: "Email address is required" } }, true);
    } else if (!isValidEmail(email.value.trim())) {
      verifiedForm = mergeForm({ email: { ...email, error: "Invalid email address" } }, true);
    } else {
      verifiedForm = mergeForm({ email: { ...email, error: "" } });
    }

    // Password
    if (!isValidPassword(password.value)) {
      verifiedForm = mergeForm(
        { password: { ...password, error: "Password must be at least 8 characters, with at least one letter and one number" } },
        true,
      );
    } else {
      verifiedForm = mergeForm({ password: { ...password, error: "" } });
    }

    // Summoner Name
    if (!inputRiotUserName || !inputRiotTagLine) {
      verifiedForm = mergeForm({ riotId: { ...riotId, error: "Riot ID is required" } }, true);
    } else {
      verifiedForm = mergeForm({ riotId: { ...riotId, error: "" } });
    }

    // Clear signupError if a validation error is found
    if (foundValidationError) {
      setSignupError("");
    }

    const newForm = { ...form, ...verifiedForm };
    return { form: newForm, error: hasError };
  };

  const completeSignup = (e) => {
    e.persist();
    const verifiedForm = verifyForm();
    setForm({ ...form, ...verifiedForm.form });

    if (verifiedForm.error) return;

    setSignupError("");
    createAccount();
  };

  // Oauth Login
  const [initiateOauthLogin, oauthState] = useInitiateOauthLoginLazyQuery();

  useEffect(() => {
    if (oauthState.data?.initiateOauthLogin?.url) {
      window.location.href = oauthState.data.initiateOauthLogin.url;
    }
  }, [oauthState.data]);

  const handleOauthSignup = (providerName) => {
    initiateOauthLogin({ variables: { providerName } });
  };

  return (
    <div className="relative w-full overflow-hidden">
      {/* Animated video background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="hidden xs:block fixed top-0 left-0 w-screen h-screen object-cover object-top z-0 pointer-events-none select-none"
        tabIndex={-1}
        aria-hidden="true"
        src="https://static.bigbrain.gg/assets/lol/backgrounds/irelia_animated_bg.webm"
      />
      {/* Overlay for darkening effect */}
      <div
        className="hidden xs:block fixed top-0 left-0 w-screen h-screen z-10 pointer-events-none select-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(7,7,32,0.14) 0%, rgba(7,7,32,0.22) 40%, rgba(7,7,32,0.45) 80%, rgba(7,7,32,0.7) 100%), linear-gradient(to bottom, rgba(7,7,32,0.05) 0%, rgba(7,7,32,0.05) 85%, rgba(7,7,32,0.3) 95%, rgba(7,7,32,0.45) 100%)",
        }}
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="w-full mt-[20px] xs:mt-[70px] xs:ml-[70px] z-20 relative flex max-xs:justify-center">
        <Helmet>
          <title>U.GG - Signup</title>
        </Helmet>
        <div
          className="w-full rounded-[4px] max-w-[396px] pt-[20px] pr-[48px] pb-[48px] pl-[48px]"
          style={{
            background: "rgba(7, 7, 32, 0.4)",
            backdropFilter: "blur(15px)",
            WebkitBackdropFilter: "blur(15px)",
            textAlign: "left",
          }}
        >
          <div className="flex xs:mt-[8px] mb-[24px] text-[14px] text-lavender-50">
            <Link to={AppRoutes.HOME}>&lt; Back to Home</Link>
          </div>
          <img className="flex mb-[32px] w-[80px]" src="https://static.bigbrain.gg/assets/ugg/logo/ugg-logo.svg" />
          <div className="text-white text-[30px] font-bold font-barlow">Create an Account</div>
          <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">to Get Started</div>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="w-full xs:block flex flex-col min-h-[62vh] xs:min-h-0"
            style={{ textAlign: "left" }}
          >
            {signupError && <div className="_error-msg mb-[8px] text-[14px] text-accent-red-450 font-[500]">{signupError}</div>}
            <CustomInput
              className="
                  mb-[12px] [&_input]:h-[45px] [&_input]:w-full [&_input]:max-w-[300px]
                  [&_input]:text-[16px] [&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0
                  [&_input]:bg-[rgba(7,7,32,0.2)] xs:[&_input]:bg-[rgba(7,7,32,0.2)] [&_input]:pl-4 [&_input:focus]:outline-none [&_input:focus]:border-0
                  [&_input]:placeholder:text-lavender-50 [&_input]:placeholder:font-normal
                  [&_.input-label]:text-[16px] [&_.input-label]:mb-[10px] [&_.input-label]:text-lavender-50 [&_.input-label]:font-inter
                  max-sm:[&_input]:bg-purple-100
                "
              value={email.value}
              error={email.error}
              onChange={(e) => onInputChange("email", { value: e.target.value })}
              placeholder="Email"
              type="email"
              tabIndex={1000}
              autoFocus
            />
            <CustomInput
              className="
                  mb-[12px] [&_input]:h-[45px] [&_input]:w-full [&_input]:max-w-[300px]
                  [&_input]:text-[16px] [&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0
                  [&_input]:bg-[rgba(7,7,32,0.2)] xs:[&_input]:bg-[rgba(7,7,32,0.2)] [&_input]:pl-4 [&_input:focus]:outline-none [&_input:focus]:border-0
                  [&_input]:placeholder:text-lavender-50 [&_input]:placeholder:font-normal
                  [&_.input-label]:text-[16px] [&_.input-label]:mb-[10px] [&_.input-label]:text-lavender-50 [&_.input-label]:font-inter
                  max-sm:[&_input]:bg-purple-100
                "
              value={password.value}
              error={password.error}
              onChange={(e) => onInputChange("password", { value: e.target.value })}
              onFocus={onPasswordFocus}
              onBlur={onPasswordBlur}
              placeholder="Password"
              tabIndex={1001}
              type="password"
              maxLength={128}
            />
            {showPasswordChecker && (
              <div className="mt-[12px]">
                <PasswordChecker password={password.value} />
              </div>
            )}
            <div className="w-full mt-[12px] mb-[24px]">
              {riotId.error && <div className="mb-[3px] text-accent-orange-500 text-[14px] font-medium">{riotId.error}</div>}
              <SummonerFinder
                className="
                  [&_input]:h-[45px] [&_input]:w-full [&_input]:max-w-[300px]
                  [&_input]:text-[16px] [&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0
                  [&_input]:bg-[rgba(7,7,32,0.2)] xs:[&_input]:bg-[rgba(7,7,32,0.2)] [&_input]:pl-4 [&_input:focus]:outline-none [&_input:focus]:border-0
                  [&_input]:placeholder:text-lavender-50 [&_input]:placeholder:font-normal
                  [&_.input-label]:text-[16px] [&_.input-label]:mb-[10px] [&_.input-label]:text-lavender-50 [&_.input-label]:font-inter
                  max-sm:[&_input]:bg-purple-100
                "
                placeholder={"Summoner Name"}
                riotId={riotId.value}
                summonerRegion={summonerRegion.value}
                onSummonerChange={(e) => onInputChange("riotId", { value: e.target.value })}
                onRegionChange={(region) => onInputChange("summonerRegion", { value: region || "" })}
                tabIndex={1002}
                error={riotId.error}
              />
            </div>
            <ButtonSuccess
              className="btn-blue h-[42px] !text-[14px]"
              label="Create Account"
              isLoading={creatingAccount}
              onClick={completeSignup}
              bForceSubmitOnEnter={true}
            />
            <div className="flex items-center my-[20px]">
              <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
              <span className="mx-2 text-[10px] text-lavender-50">OR</span>
              <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
            </div>
            <div className="mb-[20px] flex justify-start">
              <button
                type="button"
                onClick={() => handleOauthSignup("riot")}
                className="bg-[#eb0029] rounded-[4px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
                aria-label="Signup with Riot"
              >
                <Icon icon={<SvgBrandsRiot />} className="w-[20px] h-[20px]" />
              </button>
              <button
                type="button"
                onClick={() => handleOauthSignup("google")}
                className="bg-[#f7f7f7] rounded-[4px] ml-[12px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
                aria-label="Signup with Google"
              >
                <Icon icon={<SvgBrandsGoogle />} className="w-[20px] h-[20px]" />
              </button>
              <button
                type="button"
                onClick={() => handleOauthSignup("discord")}
                className="bg-[#5865F2] rounded-[4px] ml-[12px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
                aria-label="Signup with Discord"
              >
                <Icon icon={<SvgLolDiscordIcon />} className="w-[20px] h-[20px]" />
              </button>
            </div>
            <div className="text-lavender-50 text-[14px] font-inter mb-[12px] xs:mb-[48px]">
              Already have an account?{" "}
              <Link className="underline text-accent-blue-400 font-bold" to={{ pathname: AppRoutes.LOGIN, search }}>
                <span>Log In.</span>
              </Link>
            </div>
            <div className="text-accent-gray-200 text-[14px] font-inter mt-auto xs:pb-0">
              U.GG isn’t endorsed by Riot Games and doesn’t reflect the views or opinions of Riot Games or anyone officially
              involved in producing or managing League of Legends.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
